import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "@mui/icons-material";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { getAccountsList } from "../firebase/dataHandler";
import { generatePDFReport } from "../helpers/pdfReportHelper";
import { generateXLSXReport } from "../helpers/xlsxReportHelper";
import MonthAndYearPicker from "../components/monthPicker/monthAndYearPicker";
import PremiumFeatureLock from "../components/PremiumFeatureLock";
import { useTranslation } from 'react-i18next';

function Reports() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState(null);
  const [filterDate, setFilterDate] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const [reportFilter, setReportFilter] = useState(false);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  const [reportTablePage, setReportTablePage] = useState(0);
  const [reportTableRowsPerPage, setReportTableRowsPerPage] = useState(10);
  const [reportFinalData, setReportFinalData] = useState([]);

  useEffect(() => {
    getAccountsList()
      .then((accountList) => {
        setAccounts({
          list: accountList,
          length: Object.values(accountList).filter((a) => a.isActive).length,
          entries: Object.keys(accountList).map((keys) => {
            return {
              [`${keys}`]: accountList[keys].entries,
            };
          }),
        });
      })
      .catch((e) => alert(e));
  }, []);

  const reportCol = [
    { id: "s_no", label: "S.No." },
    { id: "date", label: t('reports.date') },
    { id: "accountId", label: t('reports.account') },
    { id: "name", label: "Name" },
    { id: "amount", label: t('reports.amount') + " (₹)" },
    { id: "interestPercentage", label: t('reports.interest') + " (%)" },
    { id: "interestAmount", label: t('reports.interest') + " (₹)" },
    { id: "totalAmount", label: t('reports.total') + " (₹)" },
    { id: "installmentPeriod", label: "Installment Period" },
    { id: "installmentAmount", label: "Inst. Amount (₹)" },
    { id: "returnedAmount", label: "Paid (₹)" },
    { id: "balanceAmount", label: "Balance (₹)" },
    { id: "remainingPeriod", label: "Remaining Period" },
    { id: "returnPercentage", label: "Return %" },
  ];

  const createRowData = (account) => {
    let paidDayCount = account.entries
      ? Object.values(account.entries).reduce((acc, sum) => acc + (sum.isUpfrontInterest ? 0 : Number(sum.dayCount || 0)), 0)
      : 0;
    let returnedAmount = account.entries
      ? Object.values(account.entries).reduce((acc, sum) => acc + Number(sum.amountPaid || 0), 0)
      : 0;
    let hasUpfront = account.collectUpfrontInterest || (account.entries && Object.values(account.entries).some(e => e.isUpfrontInterest));
    let principal = Number(account.amount || 0);
    let interestAmount = Number(account.interestAmount || 0);
    let totalAmount = (Number(account.installmentAmount || 0) * Number(account.installmentPeriod || 0)) || (hasUpfront ? principal : (principal + interestAmount));
    let returnPct = principal > 0 ? ((returnedAmount / principal) * 100).toFixed(2) : 0;
    
    return {
      date: account.date,
      accountId: account.accountId,
      name: account.name,
      amount: principal,
      interestPercentage: Number(account.interestPercentage || 0),
      interestAmount: interestAmount,
      totalAmount: totalAmount,
      installmentPeriod: Number(account.installmentPeriod || 0),
      installmentAmount: Number(account.installmentAmount || 0),
      returnedAmount,
      balanceAmount: Math.max(0, totalAmount - returnedAmount),
      remainingPeriod: Math.max(0, Number(account.installmentPeriod || 0) - paidDayCount),
      returnPercentage: returnPct + " %",
    };
  };

  useEffect(() => {
    if (accounts) {
      setReportFinalData(
        Object.values(accounts.list)
          .filter(({ date }) =>
            reportFilter
              ? new Date(date).getMonth() === filterDate.month &&
                new Date(date).getFullYear() === filterDate.year
              : true
          )
          .filter((a) => a.isActive || a.isActive === undefined)
          .map((account) => createRowData(account))
      );
    }
  }, [accounts, reportFilter, filterDate]);

  const currencyFormat = (value) => value.toLocaleString("en-IN", { maximumFractionDigits: 3 });

  const getInvestmentSheetData = (account) => {
    let paidAmount = account.entries
      ? Object.values(account.entries).reduce((acc, { amountPaid }) => acc + Number(amountPaid || 0), 0)
      : 0;
    let paidDayCount = account.entries
      ? Object.values(account.entries).reduce((acc, { dayCount, isUpfrontInterest }) => acc + (isUpfrontInterest ? 0 : Number(dayCount || 0)), 0)
      : 0;
      
    let hasUpfront = account.collectUpfrontInterest || (account.entries && Object.values(account.entries).some(e => e.isUpfrontInterest));
    let principal = Number(account.amount || 0);
    let interestAmount = Number(account.interestAmount || 0);
    let totalAmount = (Number(account.installmentAmount || 0) * Number(account.installmentPeriod || 0)) || (hasUpfront ? principal : (principal + interestAmount));
    let returnPct = principal > 0 ? ((paidAmount / principal) * 100).toFixed(2) : 0;

    return [
      new Date(account.date).toLocaleDateString("en-IN"),
      account.accountId.split("_")[0],
      account.name,
      principal,
      Number(account.interestPercentage || 0),
      interestAmount,
      totalAmount,
      Number(account.installmentPeriod || 0),
      Number(account.installmentAmount || 0),
      paidDayCount,
      paidAmount,
      Math.max(0, Number(account.installmentPeriod || 0) - paidDayCount),
      Math.max(0, totalAmount - paidAmount),
      returnPct + " %",
    ];
  };

  const [viewTab, setViewTab] = useState(0); // 0: Portfolio Accounts, 1: All Transactions Stream
  const [txnTypeFilter, setTxnTypeFilter] = useState("ALL"); // ALL, OUTGOING, INCOMING, UPFRONT

  const getAllTransactions = (accountsList) => {
    if (!accountsList) return [];
    let txns = [];
    accountsList
      .filter((a) => a.isActive || a.isActive === undefined)
      .forEach((account) => {
        // Outgoing: Initial Loan Disbursement
        txns.push({
          date: account.date,
          accountId: account.accountId.split("_")[0],
          rawAccountId: account.accountId,
          name: account.name,
          tag: "Loan Disbursed",
          direction: "OUTGOING",
          amount: Number(account.amount || 0),
          installmentAmount: Number(account.installmentAmount || 0),
        });

        // Incoming: Entries (Upfront interest & regular repayments)
        if (account.entries) {
          Object.values(account.entries).forEach((entry) => {
            txns.push({
              date: entry.date,
              accountId: account.accountId.split("_")[0],
              rawAccountId: account.accountId,
              name: account.name,
              tag: entry.isUpfrontInterest ? "Upfront Interest" : "Repayment Collected",
              direction: "INCOMING",
              amount: Number(entry.amountPaid || 0),
              installmentAmount: Number(account.installmentAmount || 0),
              dayCount: Number(entry.dayCount || 0),
            });
          });
        }
      });
    return txns.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const generateReport = (accounts, filter, exportType, format = "excel") => {
    let activeAccounts = accounts.filter(a => a.isActive || a.isActive === undefined);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    // 1. Filter Investments (Disbursed accounts) based on period
    let periodInvestments = activeAccounts;
    if (exportType === "today") {
      const todayStr = new Date().toDateString();
      periodInvestments = activeAccounts.filter(a => new Date(a.date).toDateString() === todayStr);
    } else if (exportType === "week") {
      const now = new Date();
      const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      periodInvestments = activeAccounts.filter(a => new Date(a.date) >= startOfWeek);
    } else if (exportType === "month" || exportType === "month_investment") {
      periodInvestments = activeAccounts.filter(a =>
        new Date(a.date).getMonth() === filterDate.month &&
        new Date(a.date).getFullYear() === filterDate.year
      );
    }

    // 2. Filter Collections / Transactions based on period
    let allTxnsData = getAllTransactions(accounts);
    if (exportType === "today") {
      const todayStr = new Date().toDateString();
      allTxnsData = allTxnsData.filter(e => new Date(e.date).toDateString() === todayStr);
    } else if (exportType === "week") {
      const now = new Date();
      const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      allTxnsData = allTxnsData.filter(e => new Date(e.date) >= startOfWeek);
    } else if (exportType === "month") {
      allTxnsData = allTxnsData.filter(e =>
        new Date(e.date).getMonth() === filterDate.month &&
        new Date(e.date).getFullYear() === filterDate.year
      );
    }

    // Prepare Sheet Data for Investments
    let investmentSheetRows = periodInvestments.map((account) => getInvestmentSheetData(account));

    // Prepare Sheet Data for Collections
    let collectionTxns = allTxnsData.filter(t => t.direction === "INCOMING");
    let entriesSheetDataArr = allTxnsData.map((txn) => [
      new Date(txn.date).toLocaleDateString("en-IN"),
      txn.accountId,
      txn.name,
      txn.tag,
      txn.direction,
      txn.direction === "OUTGOING" ? -txn.amount : txn.amount,
    ]);

    // Totals calculations
    const invCount = investmentSheetRows.length || 1;
    let tAmount = investmentSheetRows.reduce((acc, sum) => acc + (sum[3] || 0), 0);
    let tPercent = investmentSheetRows.reduce((acc, sum) => acc + (sum[4] || 0), 0) / invCount;
    let tInterest = investmentSheetRows.reduce((acc, sum) => acc + (sum[5] || 0), 0);
    let tHanded = investmentSheetRows.reduce((acc, sum) => acc + (sum[6] || 0), 0);
    let tPaid = investmentSheetRows.reduce((acc, sum) => acc + (sum[10] || 0), 0);
    let tBalance = investmentSheetRows.reduce((acc, sum) => acc + (sum[12] || 0), 0);
    let totalReturnPct = tAmount > 0 ? ((tPaid / tAmount) * 100).toFixed(2) : 0;

    let tOutgoing = allTxnsData.filter(t => t.direction === "OUTGOING").reduce((sum, t) => sum + t.amount, 0);
    let tIncoming = collectionTxns.reduce((sum, t) => sum + t.amount, 0);
    let netFlow = tIncoming - tOutgoing;

    if (exportType === "analytical") {
      const today = new Date();
      const defaulters = accounts.filter(a => a.isActive !== false).map(a => {
        let pDays = a.entries ? Object.values(a.entries).reduce((acc, e) => acc + Number(e.dayCount || 0), 0) : 0;
        let elDays = Math.round((today.getTime() - new Date(a.date).getTime()) / 86400000);
        let exDays = a.installmentType === 'weekly' ? Math.floor(elDays / 7) : a.installmentType === 'monthly' ? Math.floor(elDays / 30) : elDays;
        exDays = Math.min(exDays, a.installmentPeriod || 0);
        return { ...a, unpaidPeriods: exDays - pDays, paidDayCount: pDays };
      }).filter(a => a.unpaidPeriods > 0).sort((a, b) => b.unpaidPeriods - a.unpaidPeriods).slice(0, 20);

      const defSheet = defaulters.map(d => [d.accountId.split('_')[0], d.name, d.installmentType || 'daily', d.unpaidPeriods, d.paidDayCount, d.installmentPeriod]);
      
      if (format === "excel") {
        generateXLSXReport({
          sheets: [
            {
              name: "Defaulters Summary",
              data: [
                ["Analytical Report - Portfolio Overview"],
                ["Total Disbursed (₹)", "Total Recovered (₹)", "Overall Return %"],
                [tAmount, tPaid, totalReturnPct + "%"],
                [],
                ["Top Defaulters"],
                ["Account Id", "Name", "Type", "Unpaid Periods", "Paid Periods", "Total Periods"],
                ...defSheet
              ]
            }
          ]
        }, `Finaculator_Analytical_Report.xlsx`);
      } else if (format === "pdf") {
        const headers = ["Account Id", "Name", "Type", "Unpaid Periods", "Paid Periods", "Total Periods"];
        const totalsRow = ["", "", "Portfolio Return:", totalReturnPct + "%", "", ""];
        generatePDFReport(exportType, `Analytical Report - Top Defaulters`, headers, defSheet, totalsRow);
      }
      return;
    }

    if (format === "excel") {
      const selectedMonthName = monthNames[filterDate.month];
      let reportTitle = exportType === "month" ? `${selectedMonthName}_${filterDate.year}` : exportType.toUpperCase();
      let filename = `Finaculator_${reportTitle}_Report.xlsx`;

      let investmentHeaders = [
        "Date", "Account Id", "Name", "Principal Amount (₹)", "Interest Percentage (%)", "Interest Payable (₹)",
        "Total Collectible (₹)", "Installment Period", "Equated Installment (₹)", "Recovered Installments",
        "Amount Recovered (₹)", "Remaining Period", "Outstanding Balance (₹)", "Return Percentage (%)"
      ];

      let collectionsHeaders = [
        "Date", "Account Id", "Name", "Transaction Tag", "Direction", "Amount (₹)"
      ];

      generateXLSXReport({
        sheets: [
          {
            name: `Investments (${periodInvestments.length})`,
            data: [
              investmentHeaders,
              ...investmentSheetRows.sort((a, b) => new Date(a[0]) - new Date(b[0])),
              [], [],
              [
                "", "", "Totals & Averages", tAmount, tPercent.toFixed(2) + " %", tInterest,
                tHanded, "", "", "", tPaid, "", tBalance, totalReturnPct + " %"
              ]
            ]
          },
          {
            name: `Collections (${allTxnsData.length})`,
            data: [
              collectionsHeaders,
              ...entriesSheetDataArr,
              [], [],
              ["", "", "", "Totals", `Out: ₹${tOutgoing} | In: ₹${tIncoming}`, `Net Cashflow: ₹${netFlow}`]
            ]
          }
        ]
      }, filename);
    } else if (format === "pdf") {
      if (exportType === "today" || exportType === "week" || exportType === "month") {
        const headers = ["Date", "Account Id", "Name", "Transaction Tag", "Direction", "Amount (₹)"];
        const totalsRow = ["", "", "", "Totals", `Out: Rs ${tOutgoing} | In: Rs ${tIncoming}`, `Net: Rs ${netFlow}`];
        const title = `Collections & Ledger (${exportType.toUpperCase()})`;
        generatePDFReport(exportType, title, headers, entriesSheetDataArr, totalsRow);
      } else {
        const headers = [
          "Date", "Account", "Name", "Principal", "Int %", "Int ₹", "Collectible", "Period", "EMI", "Rec. Per.", "Rec. Amt", "Remain", "Balance", "Return %"
        ];
        const totalsRow = [
          "", "", "Totals", "Rs " + tAmount, tPercent.toFixed(2) + "%", "Rs " + tInterest, "Rs " + tHanded, "", "", "", "Rs " + tPaid, "", "Rs " + tBalance, totalReturnPct + "%"
        ];
        const title = `Portfolio Report (${exportType.toUpperCase()})`;
        generatePDFReport(exportType, title, headers, investmentSheetRows, totalsRow);
      }
    }
  };

  // Computed all transactions for UI stream
  const allTxns = useMemo(() => {
    if (!accounts) return [];
    return getAllTransactions(Object.values(accounts.list)).filter((t) => {
      // Date filter
      const matchesDate = reportFilter
        ? new Date(t.date).getMonth() === filterDate.month &&
          new Date(t.date).getFullYear() === filterDate.year
        : true;
      if (!matchesDate) return false;

      // Type filter
      if (txnTypeFilter === "OUTGOING") return t.direction === "OUTGOING";
      if (txnTypeFilter === "INCOMING") return t.direction === "INCOMING" && t.tag === "Repayment Collected";
      if (txnTypeFilter === "UPFRONT") return t.tag === "Upfront Interest";
      return true;
    });
  }, [accounts, reportFilter, filterDate, txnTypeFilter]);

  const totalOutgoingUI = useMemo(() => allTxns.filter(t => t.direction === "OUTGOING").reduce((acc, t) => acc + t.amount, 0), [allTxns]);
  const totalIncomingUI = useMemo(() => allTxns.filter(t => t.direction === "INCOMING").reduce((acc, t) => acc + t.amount, 0), [allTxns]);

  return (
    <div className="page-pad fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 4px 16px" }}>
        <div>
          <h1 style={{ fontSize: 28, marginBottom: 4 }}>{t('reports.title')}</h1>
          <small style={{ color: "var(--text-muted)", fontSize: 12 }}>{t('reports.performance_report')}</small>
        </div>
      </div>

      <PremiumFeatureLock 
        title="Advanced Reports & Export"
        description="Take full control of your business data with comprehensive reporting tools."
        features={[
          'Export reports in PDF, Excel, and CSV formats',
          'Multilingual support (Available in English & Tamil)',
          'Detailed profit & loss tracking',
          'Customizable date ranges and filters'
        ]}
      >
        <div style={{ padding: "0 4px" }}>
          {/* View Tab Switcher */}
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <Button
              variant={viewTab === 0 ? "contained" : "outlined"}
              size="small"
              onClick={() => setViewTab(0)}
              style={{
                borderRadius: 10,
                textTransform: "none",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                fontWeight: 600,
                backgroundColor: viewTab === 0 ? "var(--accent-green)" : "transparent",
                borderColor: "var(--accent-green)",
                color: viewTab === 0 ? "#fff" : "var(--text-primary)",
              }}
            >
              Accounts Overview
            </Button>
            <Button
              variant={viewTab === 1 ? "contained" : "outlined"}
              size="small"
              onClick={() => setViewTab(1)}
              style={{
                borderRadius: 10,
                textTransform: "none",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                fontWeight: 600,
                backgroundColor: viewTab === 1 ? "var(--accent-green)" : "transparent",
                borderColor: "var(--accent-green)",
                color: viewTab === 1 ? "#fff" : "var(--text-primary)",
              }}
            >
              All Transactions Feed (In & Out)
            </Button>
          </div>

          {/* Controls */}
          <div
            style={{
              display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
              padding: "8px 0", marginBottom: 12,
            }}
          >
            <MonthAndYearPicker value={filterDate} onChange={(value) => setFilterDate(value)} />

            <div
              style={{
                display: "flex", alignItems: "center", gap: 4, background: "var(--bg-card)",
                borderRadius: 10, padding: "4px 10px", border: "1px solid var(--border-subtle)",
                fontSize: 12, color: "var(--text-secondary)",
              }}
            >
              <span>{t('reports.filter_by_type')}</span>
              <Switch size="small" onClick={() => setReportFilter(!reportFilter)} />
            </div>

            {viewTab === 1 && (
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { label: "All", value: "ALL" },
                  { label: "🔴 Outgoing (Loans)", value: "OUTGOING" },
                  { label: "🟢 Incoming (Repayments)", value: "INCOMING" },
                  { label: "⚡ Upfront Interest", value: "UPFRONT" },
                ].map((item) => (
                  <Button
                    key={item.value}
                    size="small"
                    variant={txnTypeFilter === item.value ? "contained" : "text"}
                    onClick={() => setTxnTypeFilter(item.value)}
                    style={{
                      borderRadius: 8,
                      fontSize: 11,
                      textTransform: "none",
                      padding: "2px 8px",
                      backgroundColor: txnTypeFilter === item.value ? "var(--bg-elevated)" : "transparent",
                      color: txnTypeFilter === item.value ? "var(--accent-green)" : "var(--text-muted)",
                      border: "1px solid var(--border-subtle)",
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            )}

            {accounts && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download />}
                  onClick={(e) => setExportAnchorEl(e.currentTarget)}
                  style={{
                    borderColor: "rgba(45,132,255,0.4)", color: "#2d84ff", borderRadius: 10,
                    textTransform: "none", fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 600, padding: "5px 14px",
                    marginLeft: "auto",
                  }}
                >
                  {t('reports.export_reports')}
                </Button>
                <Menu
                  anchorEl={exportAnchorEl}
                  open={Boolean(exportAnchorEl)}
                  onClose={() => setExportAnchorEl(null)}
                  PaperProps={{
                    style: { borderRadius: 12, border: "1px solid var(--border-subtle)", background: "var(--bg-card)", color: "var(--text-primary)" },
                  }}
                >
                  <MenuItem disabled style={{ opacity: 1, fontWeight: 'bold', fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Collections & Loan Ledger</MenuItem>
                  <MenuItem onClick={() => { setExportAnchorEl(null); generateReport(Object.values(accounts.list), null, 'today', 'excel'); }} style={{ fontSize: 13, fontFamily: "Inter" }}>Export Today's Ledger (Excel)</MenuItem>
                  <MenuItem onClick={() => { setExportAnchorEl(null); generateReport(Object.values(accounts.list), null, 'today', 'pdf'); }} style={{ fontSize: 13, fontFamily: "Inter" }}>Export Today's Ledger (PDF)</MenuItem>
                  <MenuItem onClick={() => { setExportAnchorEl(null); generateReport(Object.values(accounts.list), null, 'week', 'excel'); }} style={{ fontSize: 13, fontFamily: "Inter" }}>Export Current Week Ledger (Excel)</MenuItem>
                  <MenuItem onClick={() => { setExportAnchorEl(null); generateReport(Object.values(accounts.list), null, 'week', 'pdf'); }} style={{ fontSize: 13, fontFamily: "Inter" }}>Export Current Week Ledger (PDF)</MenuItem>
                  <MenuItem onClick={() => { setExportAnchorEl(null); generateReport(Object.values(accounts.list), null, 'month', 'excel'); }} style={{ fontSize: 13, fontFamily: "Inter" }}>Export Current Month Ledger (Excel)</MenuItem>
                  <MenuItem onClick={() => { setExportAnchorEl(null); generateReport(Object.values(accounts.list), null, 'month', 'pdf'); }} style={{ fontSize: 13, fontFamily: "Inter" }}>Export Current Month Ledger (PDF)</MenuItem>
                  
                  <div style={{ height: 1, background: 'var(--border-subtle)', margin: '4px 0' }} />
                  
                  <MenuItem disabled style={{ opacity: 1, fontWeight: 'bold', fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Complete Portfolio</MenuItem>
                  <MenuItem onClick={() => { setExportAnchorEl(null); generateReport(Object.values(accounts.list), null, 'complete', 'excel'); }} style={{ fontSize: 13, fontFamily: "Inter" }}>Export as Excel</MenuItem>
                  <MenuItem onClick={() => { setExportAnchorEl(null); generateReport(Object.values(accounts.list), null, 'complete', 'pdf'); }} style={{ fontSize: 13, fontFamily: "Inter" }}>Export as PDF</MenuItem>

                  <div style={{ height: 1, background: 'var(--border-subtle)', margin: '4px 0' }} />
                  
                  <MenuItem disabled style={{ opacity: 1, fontWeight: 'bold', fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Analytical & Trends</MenuItem>
                  <MenuItem onClick={() => { setExportAnchorEl(null); generateReport(Object.values(accounts.list), null, 'analytical', 'excel'); }} style={{ fontSize: 13, fontFamily: "Inter" }}>Export Analytical Report (Excel)</MenuItem>
                  <MenuItem onClick={() => { setExportAnchorEl(null); generateReport(Object.values(accounts.list), null, 'analytical', 'pdf'); }} style={{ fontSize: 13, fontFamily: "Inter" }}>Export Analytical Report (PDF)</MenuItem>
                </Menu>
              </>
            )}
          </div>

          {/* View Tab 0: Portfolio Accounts Table */}
          {viewTab === 0 && accounts && (
            <TableContainer
              component={Paper}
              style={{
                background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: 16, boxShadow: "none",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow style={{ background: "var(--bg-elevated)" }}>
                    {reportCol.map((col) => (
                      <TableCell
                        key={col.id}
                        align={col.align}
                        style={{
                          color: "var(--text-secondary)", fontFamily: "Inter, sans-serif", fontSize: 11,
                          fontWeight: 600, borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap", padding: "10px 12px",
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(reportTableRowsPerPage > 0
                    ? reportFinalData.slice(reportTablePage * reportTableRowsPerPage, reportTablePage * reportTableRowsPerPage + reportTableRowsPerPage)
                    : reportFinalData
                  ).map((row, i) => (
                    <TableRow key={i} style={{ transition: "background 0.15s" }} hover>
                      {reportCol.map(({ id: data, label }, j) => (
                        <TableCell
                          key={j}
                          style={{
                            whiteSpace: "nowrap", fontFamily: "Inter, sans-serif", fontSize: 12,
                            color: data === "balanceAmount" ? "var(--accent-red)" : data === "returnedAmount" || data === "returnPercentage" ? "var(--accent-green)" : "var(--text-primary)",
                            borderBottom: "1px solid var(--border-subtle)", padding: "9px 12px",
                          }}
                        >
                          {data === "s_no" ? i + 1 : data === "accountId" ? (
                            <Link to={`/account/${row[data]}`} style={{ color: "var(--accent-blue)", textDecoration: "none", fontWeight: 600 }}>
                              {row[data].split("_")[0]}
                            </Link>
                          ) : label.includes("₹") && typeof row[data] === "number" ? (
                            currencyFormat(row[data])
                          ) : (
                            row[data]
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
                      colSpan={14}
                      count={reportFinalData.length}
                      rowsPerPage={reportTableRowsPerPage}
                      page={reportTablePage}
                      SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
                      onRowsPerPageChange={(event) => {
                        setReportTableRowsPerPage(Number(event.target.value));
                        setReportTablePage(0);
                      }}
                      onPageChange={(event, newPage) => setReportTablePage(newPage)}
                      style={{ color: "var(--text-secondary)", fontFamily: "Inter, sans-serif", borderBottom: "none" }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}

          {/* View Tab 1: All Transactions Stream */}
          {viewTab === 1 && accounts && (
            <Box>
              {/* Summary Cards */}
              <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1, background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: 12, padding: "12px 16px" }}>
                  <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Total Outgoing (Loans Given)</p>
                  <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#ef4444" }}>₹{totalOutgoingUI.toLocaleString("en-IN")}</p>
                </div>
                <div style={{ flex: 1, background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: 12, padding: "12px 16px" }}>
                  <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Total Incoming (Collected)</p>
                  <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "var(--accent-green)" }}>₹{totalIncomingUI.toLocaleString("en-IN")}</p>
                </div>
                <div style={{ flex: 1, background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: 12, padding: "12px 16px" }}>
                  <p style={{ margin: 0, fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Net Cashflow</p>
                  <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: totalIncomingUI - totalOutgoingUI >= 0 ? "var(--accent-green)" : "#ef4444" }}>
                    ₹{(totalIncomingUI - totalOutgoingUI).toLocaleString("en-IN")}
                  </p>
                </div>
              </div>

              {/* Transactions Table */}
              <TableContainer
                component={Paper}
                style={{
                  background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: 16, boxShadow: "none",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ background: "var(--bg-elevated)" }}>
                      <TableCell style={{ color: "var(--text-secondary)", fontSize: 11, fontWeight: 600 }}>S.No.</TableCell>
                      <TableCell style={{ color: "var(--text-secondary)", fontSize: 11, fontWeight: 600 }}>Date</TableCell>
                      <TableCell style={{ color: "var(--text-secondary)", fontSize: 11, fontWeight: 600 }}>Account ID</TableCell>
                      <TableCell style={{ color: "var(--text-secondary)", fontSize: 11, fontWeight: 600 }}>Name</TableCell>
                      <TableCell style={{ color: "var(--text-secondary)", fontSize: 11, fontWeight: 600 }}>Transaction Tag</TableCell>
                      <TableCell style={{ color: "var(--text-secondary)", fontSize: 11, fontWeight: 600 }}>Direction</TableCell>
                      <TableCell align="right" style={{ color: "var(--text-secondary)", fontSize: 11, fontWeight: 600 }}>Amount (₹)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allTxns.map((txn, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell style={{ fontSize: 12, color: "var(--text-secondary)" }}>{idx + 1}</TableCell>
                        <TableCell style={{ fontSize: 12, color: "var(--text-primary)", whiteSpace: "nowrap" }}>
                          {new Date(txn.date).toLocaleDateString("en-IN")}
                        </TableCell>
                        <TableCell style={{ fontSize: 12 }}>
                          <Link to={`/account/${txn.rawAccountId}`} style={{ color: "var(--accent-blue)", textDecoration: "none", fontWeight: 600 }}>
                            {txn.accountId}
                          </Link>
                        </TableCell>
                        <TableCell style={{ fontSize: 12, color: "var(--text-primary)" }}>{txn.name}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: "3px 8px",
                              borderRadius: 6,
                              backgroundColor: txn.direction === "OUTGOING" ? "rgba(239, 68, 68, 0.15)" : txn.tag === "Upfront Interest" ? "rgba(251, 146, 60, 0.15)" : "rgba(21, 195, 88, 0.15)",
                              color: txn.direction === "OUTGOING" ? "#ef4444" : txn.tag === "Upfront Interest" ? "#fb923c" : "var(--accent-green)",
                            }}
                          >
                            {txn.direction === "OUTGOING" ? "🔴 " : txn.tag === "Upfront Interest" ? "⚡ " : "🟢 "}
                            {txn.tag}
                          </span>
                        </TableCell>
                        <TableCell style={{ fontSize: 11, fontWeight: 700, color: txn.direction === "OUTGOING" ? "#ef4444" : "var(--accent-green)" }}>
                          {txn.direction}
                        </TableCell>
                        <TableCell align="right" style={{ fontSize: 13, fontWeight: 700, color: txn.direction === "OUTGOING" ? "#ef4444" : "var(--accent-green)" }}>
                          {txn.direction === "OUTGOING" ? `- ₹${txn.amount.toLocaleString("en-IN")}` : `+ ₹${txn.amount.toLocaleString("en-IN")}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </div>
      </PremiumFeatureLock>
    </div>
  );
}

export default Reports;
